import { useSyncExternalStore } from 'react';
import { breakpoints } from '@agentisai/design-system';

/**
 * Viewport tier — the compact (<768px) vs regular (≥768px) split used to
 * pick the profile menu's presentation: a `Menu` popover on regular tiers,
 * a bottom-sheet on compact (S-H2, decision 7a).
 *
 * App-LOCAL on purpose (S-H decision): this is the only consumer for now,
 * so it is NOT promoted to the DS. If a second `apps/ui/web` surface needs the
 * same split, generalise it into a DS `useBreakpoint()` (the
 * frontend-architecture skill's R21/R22 pattern) rather than copying this.
 *
 * `useSyncExternalStore` over `matchMedia` is the React-correct way to read
 * a browser media query: it subscribes to the live `change` event (an
 * external-system sync — frontend-architecture R24, the sanctioned use of a
 * subscription) and tears the subscription down on unmount, with a
 * server/test snapshot fallback for environments without `matchMedia`.
 */
export type ViewportTier = 'compact' | 'regular';

/** <768px is "compact" — matches the DS `breakpoints.md` boundary. */
const COMPACT_QUERY = `(max-width: ${breakpoints.md - 1}px)`;

function getMql(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(COMPACT_QUERY);
}

function subscribe(onChange: () => void): () => void {
  const mql = getMql();
  if (!mql) return () => {};
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

function getSnapshot(): ViewportTier {
  return getMql()?.matches ? 'compact' : 'regular';
}

// Server / pre-hydration snapshot: assume the regular tier (the desktop
// popover) so SSR + the first client paint agree; the client re-reads the
// real media query immediately after.
function getServerSnapshot(): ViewportTier {
  return 'regular';
}

/** Returns the current viewport tier, re-rendering on the `(max-width)` flip. */
export function useViewportTier(): ViewportTier {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
